import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";

import {
  getAuctionRules,
  getPlayerPool,
  importWomenPlayers,
  selectActiveRules,
  selectAuctionMode,
  selectModeStatus,
  selectPlayerPool,
  selectRulesAreCustom,
  selectSession,
  setAuctionMode,
  setAuctionRules,
} from "../../store/features/adminSlice";
import {
  fetchResetData,
  selectbiddingPlayersData,
} from "../../store/features/biddingPlayerSlice";
import {
  AUCTION_MODES,
  getRules,
  isWomensMode,
  MODE_ORDER,
  ruleSummary,
} from "../../config/auctionRules";
import RulesEditor from "./rulesEditor";

import Style from "./auctionMode.module.scss";

const MODES = MODE_ORDER.map((key) => AUCTION_MODES[key]);

// Lets the admin switch the whole auction between the men's and women's rule
// sets. The mode decides three things: the squad limits bidders are held to,
// the bid increments they are offered, and which collection the bidding pool is
// built from.
const AuctionMode = () => {
  const dispatch = useDispatch();
  const mode = useSelector(selectAuctionMode);
  const modeStatus = useSelector(selectModeStatus);
  const session = useSelector(selectSession);
  const playerPool = useSelector(selectPlayerPool);
  const biddingPlayers = useSelector(selectbiddingPlayersData);
  const rules = useSelector(selectActiveRules);
  const rulesAreCustom = useSelector(selectRulesAreCustom);

  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);

  // A session exists from the moment an auction is started until it is reset.
  // Both modes draw on different player pools, so the switch stays locked for
  // as long as one is open - the backend refuses it too.
  const locked = Boolean(session?._id);
  const isLive = Boolean(session?._id && session?.active);

  // Two different counts. `pool` is how many players exist in this mode's
  // source collection; `inAuction` is how many have been pulled into the
  // bidding pool. The auction cannot start until the second one is non-zero.
  const pool = playerPool?.[mode] || {};
  const inAuction = biddingPlayers?.length ?? 0;
  const needsImport = isWomensMode(mode) && !pool.total;

  useEffect(() => {
    dispatch(getPlayerPool());
    dispatch(getAuctionRules());
  }, [dispatch]);

  const switchTo = async (next) => {
    if (next === mode || busy || locked) return;

    if (
      !window.confirm(
        `Switch to the ${getRules(next).label}?\n\n` +
          `This resets the portal: every squad is emptied, purses go back to ` +
          `full, and the bidding pool is rebuilt from the ${getRules(next).short.toLowerCase()}'s players.\n\n` +
          `The ${getRules(mode).label} stays available - switch back to return to it.`
      )
    ) {
      return;
    }

    setBusy("mode");
    setNotice("");
    const res = await dispatch(setAuctionMode(next));

    if (res?.payload?.statusCode === 200) {
      // The switch rebuilt the pool and cleared every squad, so reload rather
      // than trying to patch each cached slice back into agreement.
      window.location.reload();
      return;
    }

    setBusy("");
    setNotice(res?.payload?.msg || "Could not switch mode. Please try again.");
  };

  const saveRules = async (next) => {
    setBusy("rules");
    setNotice("");
    const res = await dispatch(
      setAuctionRules({ auctionMode: mode, rules: next })
    );
    setBusy("");

    if (res?.payload?.statusCode === 200) {
      setEditing(false);
      setNotice(res.payload.msg);
    } else {
      setNotice(res?.payload?.msg || "Could not save the rules.");
    }
  };

  const resetRules = () => saveRules(null);

  const runImport = async () => {
    if (busy) return;
    setBusy("import");
    setNotice("");
    const res = await dispatch(importWomenPlayers());
    await dispatch(getPlayerPool());
    setBusy("");
    setNotice(res?.payload?.msg || "Could not import the women's player sheet.");
  };

  // Builds the bidding pool from this mode's players. Same action as the
  // refresh on the Bidding Players page, surfaced here because an empty pool
  // is what blocks the auction from starting. Resetting is the same call, so
  // it only differs by asking first: there is something to lose.
  const runFetch = async (confirmFirst = false) => {
    if (busy) return;

    if (
      confirmFirst &&
      !window.confirm(
        "Reset the player pool?\n\n" +
          "Every player uploaded from a sheet is deleted, and the pool goes " +
          "back to this mode's catalogue players only.\n\n" +
          "Every sale is undone, squads are emptied and purses go back to full."
      )
    ) {
      return;
    }

    setBusy("fetch");
    setNotice("");
    const res = await dispatch(fetchResetData());

    if (res?.payload?.statusCode === 201) {
      window.location.reload();
      return;
    }

    setBusy("");
    setNotice(res?.payload?.msg || "Could not fetch the players.");
  };

  return (
    <div className={Style.card}>
      <div className={Style.head}>
        <div>
          <div className={Style.title}>Auction mode</div>
          <div className={Style.sub}>
            Sets the squad rules, the bid increments and the player pool.
          </div>
        </div>

        <div className={Style.segmented}>
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={Boolean(busy) || locked}
              onClick={() => switchTo(m.key)}
              title={
                locked ? "Reset the auction to switch modes" : `Switch to the ${m.label}`
              }
              className={`${Style.segment} ${
                mode === m.key ? Style.segmentOn : ""
              } ${locked && mode !== m.key ? Style.segmentLocked : ""}`}
            >
              {busy === "mode" && mode !== m.key ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                m.short
              )}
            </button>
          ))}
        </div>
      </div>

      <div className={Style.rulesRow}>
        <div className={Style.rules}>
          {ruleSummary(rules).map((line) => (
            <span key={line} className={Style.rule}>
              {line}
            </span>
          ))}
          {rulesAreCustom && <span className={Style.customTag}>Edited</span>}
        </div>

        {!editing && (
          <button
            type="button"
            className={Style.editBtn}
            disabled={Boolean(busy)}
            onClick={() => {
              setNotice("");
              setEditing(true);
            }}
          >
            Edit rules
          </button>
        )}
      </div>

      {editing && (
        <RulesEditor
          // Remounts on a mode change so the draft always starts from that
          // mode's saved values.
          key={mode}
          rules={rules}
          isCustom={rulesAreCustom}
          saving={busy === "rules"}
          onCancel={() => setEditing(false)}
          onSave={saveRules}
          onReset={resetRules}
        />
      )}

      <div className={Style.pool}>
        <div className={Style.poolText}>
          {pool.total ? (
            <>
              <strong>{pool.total}</strong> {getRules(mode).short.toLowerCase()}
              {"'s"} players available. {pool.indians} Indian, {pool.overseas}{" "}
              overseas.
              {!inAuction && " None are in the auction yet."}
            </>
          ) : needsImport ? (
            "No women's players yet. Import the sheet to load them."
          ) : (
            `No ${getRules(mode).short.toLowerCase()}'s players found in the database.`
          )}
        </div>

        {/* One action, whichever is the next thing standing in the way: load
            the sheet, then pull those players into the auction. Once the pool
            is built it becomes a quiet count. */}
        {needsImport ? (
          <button
            type="button"
            className={Style.poolBtn}
            disabled={Boolean(busy)}
            onClick={runImport}
          >
            {busy === "import" ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              "Import players"
            )}
          </button>
        ) : !inAuction ? (
          <button
            type="button"
            className={Style.poolBtn}
            disabled={Boolean(busy) || !pool.total}
            onClick={() => runFetch(false)}
            title={
              pool.total
                ? "Build the bidding pool from these players"
                : "There are no players to fetch"
            }
          >
            {busy === "fetch" ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              "Fetch players"
            )}
          </button>
        ) : (
          <div className={Style.poolActions}>
            <span className={Style.poolDone}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
              {inAuction} in the auction
            </span>

            <button
              type="button"
              className={Style.poolReset}
              disabled={Boolean(busy)}
              onClick={() => runFetch(true)}
              title="Undo every sale and rebuild the pool"
            >
              {busy === "fetch" ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                "Reset pool"
              )}
            </button>
          </div>
        )}
      </div>

      {locked && (
        <div className={Style.warn}>
          {isLive
            ? "An auction is live, so the mode is locked."
            : "An auction session is open, so the mode is locked."}{" "}
          Reset the auction to switch modes.
        </div>
      )}

      {notice && (
        <div
          className={`${Style.notice} ${
            modeStatus === "error" ? Style.noticeBad : ""
          }`}
        >
          {notice}
        </div>
      )}
    </div>
  );
};

export default AuctionMode;
